import createLogger from '@/lib/logger'
import { useAuthStore } from '@/store/auth'
import {
  isAllowedEmbedHostOrigin,
  WIDGET_HOST_MESSAGE_SOURCE,
  WIDGET_MESSAGE_SOURCE,
  type HostToWidgetMessage,
} from '@/utils/embed'

const logger = createLogger('embedAuthBridge')

let started = false

/**
 * Fallback auth path for the embedded widget (the "McpChatWidget way"): when silent
 * SSO inside the iframe finds no session, ask the host window for a Keycloak access
 * token and adopt it.
 *
 * Both public GPT API paths (`apiClientPublic` and the SSE stream) read
 * `useAuthStore().accessToken`, so seeding that single value is enough — and
 * `refreshKeycloakToken()` early-returns when there is no refresh token, so an
 * injected token never triggers a stray `keycloak.login()`.
 *
 * The host cooperates by relaying whatever token it holds (see `src/embed/embed.ts`,
 * `window.EverGptWidget.setToken`). Silent SSO remains the primary path; this only
 * runs when it comes back empty, so a pure `<script>` drop-in still works unaided.
 */
export function initEmbedAuthBridge(): void {
  if (started) return
  started = true

  window.addEventListener('message', onHostMessage)
  // Announce we're up and request a token. Target '*' — the request carries no secret,
  // and inbound tokens are still origin-checked in `onHostMessage`.
  postToHost('ready')
  postToHost('request-token')
  logger.log('Embed auth bridge started, awaiting host token…')
}

function postToHost(type: 'ready' | 'request-token'): void {
  try {
    window.parent?.postMessage({ source: WIDGET_MESSAGE_SOURCE, type }, '*')
  } catch {
    // Cross-origin parent access can throw in some browsers — safe to ignore.
  }
}

function onHostMessage(event: MessageEvent): void {
  // Only our direct host (the parent frame) may hand us a token — reject sibling
  // frames, popups/openers, and self-posted messages even from an allowed origin.
  if (event.source !== window.parent) return
  if (!isAllowedEmbedHostOrigin(event.origin)) return

  const data = event.data as Partial<HostToWidgetMessage> | null
  if (!data || data.source !== WIDGET_HOST_MESSAGE_SOURCE) return

  if (
    data.type === 'set-token' &&
    typeof data.accessToken === 'string' &&
    data.accessToken
  ) {
    // Fallback only: adopt the host token ONLY while unauthenticated. If silent SSO
    // (or an earlier host token) already seeded a session, ignore this — never let a
    // later `set-token` from an allowed-origin framer override an established session
    // and fixate it onto a token the framer chose.
    if (useAuthStore().accessToken) {
      logger.log('Ignoring host token — a session is already established.')
      return
    }
    // Best-effort, single-shot: we seed only the access token (no refresh token), so
    // this session lasts exactly as long as the host's token. When it expires there is
    // no silent refresh (`refreshKeycloakToken()` early-returns without a refresh token)
    // and API/SSE calls will 401. For long-lived embeds the host must re-issue via
    // `window.EverGptWidget.setToken(...)`; the loader only relays on the initial
    // handshake, so a fresh token isn't pushed automatically.
    logger.log('Received host access token, seeding auth store.')
    useAuthStore().login({ _accessToken: data.accessToken })
  }
}
